import EntertainmentBoard from '~/components/EntertainmentBoard/EntertainmentBoard';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { useTranslation } from 'react-i18next';
import { Content, List, Root } from '@radix-ui/react-tabs';
import CustomTrigger from '~/ui/CustomTrigger/CustomTrigger';
import { useState } from 'react';
import styles from './Entertainment.module.scss';
import RecordsBoard from '~/components/Entertainment/RecordsBoard/RecordsBoard';
import { XlsBtn } from '~/ui';
import { ReportDrawer } from '~/components/ReportDrawer/ReportDrawer';
import { EntertainmentReportExtra } from './EntertainmentReportExtra/EntertainmentReportExtra';
import { downloadReportWithFields } from '~/utils/downloadReportFile';
import { Dayjs } from 'dayjs';

interface IEntertainment {
  id: number;
  name: string;
}

const ACTIVE_TABS = {
  SLOTS: 'slots',
  RECORDS: 'records',
};

const REPORT_TYPE = 'entertainment_export';

const Entertainment = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(ACTIVE_TABS.SLOTS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [entertainments, setEntertainments] = useState<IEntertainment[]>([]);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const currentFilters = {
    dates: [
      startDate?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endDate?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    ],
    ...(isAllChecked
      ? { entertainment_object_all: 1 }
      : { entertainment_object_ids: entertainments.map((item) => item.id) }),
  };

  const handleExport = async (fields: string[]) => {
    await downloadReportWithFields(
      'entertainment-export',
      'analytics-service',
      { ...currentFilters, fields }
    );
  };

  const handlePresetExport = async (presetId: number) => {
    await downloadReportWithFields(
      'entertainment-export',
      'analytics-service',
      { preset_id: presetId }
    );
  };

  return (
    <Page
      heading={
        <Heading
          text={t('services')}
          actions={
            <XlsBtn
              onClick={() => setDrawerOpen(true)}
              xlsBtnText={t('analytics_on_services')}
            />
          }
        />
      }
    >
      <Root
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
        }}
        className={styles.root}
      >
        <List className={styles.tabsList}>
          <CustomTrigger
            key={ACTIVE_TABS.SLOTS}
            value={ACTIVE_TABS.SLOTS}
            title={t('slots')}
            activeTab={activeTab}
          />
          <CustomTrigger
            key={ACTIVE_TABS.RECORDS}
            value={ACTIVE_TABS.RECORDS}
            title={t('records')}
            activeTab={activeTab}
          />
        </List>
        <Content value={ACTIVE_TABS.SLOTS}>
          <EntertainmentBoard />
        </Content>
        <Content value={ACTIVE_TABS.RECORDS}>
          <RecordsBoard />
        </Content>
      </Root>
      <ReportDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        reportType={REPORT_TYPE}
        onExport={handleExport}
        onPresetExport={handlePresetExport}
        extraParams={currentFilters}
        isExportDisabled={!startDate || !endDate}
        title={t('analytics_on_services')}
        extraContent={
          <EntertainmentReportExtra
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            entertainments={entertainments}
            onEntertainmentsChange={setEntertainments}
            isAllChecked={isAllChecked}
            onAllCheckedChange={setIsAllChecked}
          />
        }
      />
    </Page>
  );
};

export default Entertainment;
